import { Dispatch, ReactElement, SetStateAction } from 'react';
import { EAfterSuccessfulBet } from 'interfaces/betslip';
import { ERacingSportSideBarTabs } from 'interfaces/racing';
import {
  EBetCardView,
  EMultiplesView,
  EOlympicLayoutType,
  type ESportsbookLayoutTypes,
  ETopLeagueLayoutType,
  ETopMatchesLayoutType
} from 'interfaces/sportsbook-configs';
import { TCasinoCategoryItems } from './casino-data';

export type ValueOf<T> = T[keyof T];
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;
export type OptionalNullable<T> = {
  [K in keyof T as T[K] extends NonNullable<unknown> ? K : never]: T[K];
} & {
  [K in keyof T as T[K] extends NonNullable<unknown> ? never : K]?: T[K];
};

export type ActionType = {
  type: string;
  payload?: any;
};

export type DataConfig = {
  category: string;
  type: string;
  categoryIds?: string;
};

export enum ERecaptchaActions {
  getMaxBet = 'get_max_bet',
  doBet = 'do_bet',
  // this action for testing and it makes recaptcha visible,
  test_fallback = 'test_fallback'
}

export type NavSectionItem = {
  id: number;
  title: string;
  pageType?: PageType;
  iconInfo: {
    lib: IconLibName;
    name: string;
    theme?: string;
  };
  countGetter?: (countSetter: CountSetter) => string | null;
  queryStringObject?: {
    type?: string;
    sport?: string;
  };
};

export type IconLibName = 'sports' | 'flags' | 'casino' | 'generic' | 'account';

export type PageType =
  | 'all'
  | 'blank'
  | 'live'
  | 'prematch'
  | 'casinoGames'
  | 'casinoTournaments'
  | 'eLive'
  | 'ePrematch'
  | 'virtualSports'
  | 'racing'
  | 'home';

export type CountSetter = Dispatch<SetStateAction<number | null>>;

export type TSwarmGenericResponse = {
  details?: Record<string, any> | string;
  status?: number;
  result?: any;
  result_text?: string;
};

export type ResponseGeneric = {
  result: number | string;
  result_text: null | string;
  details: Record<string, any>;
};

type TCurrencyPlacement = 'default' | 'left' | 'right';

export enum EDateDayMonthOrder {
  dayFirst = 'dd/mm',
  monthFirst = 'mm/dd'
}

export type RouteMatchType = {
  url: string;
  path: string;
  isExact: boolean;
  params: Record<string, string>;
};

export type tAlignment = 'top' | 'bottom';
export type tPosition = 'right' | 'left' | 'center';

export interface ILeftSideBarConfigItem {
  imageUrl?: string | null;
  redirectPath?: string | null;
  redirectPathType?: `${number}` | null;
  target?: boolean;
}

export interface ILeftSideBarBannerConfigs {
  sports?: ILeftSideBarConfigItem;
  casino?: ILeftSideBarConfigItem;
}

export enum EFlagStyle {
  CIRCLED = 'circled',
  DEFAULT = 'default'
}

export enum ESportIconStyle {
  COLORED = 'colored',
  DEFAULT = 'default'
}

export interface IAppConfigs {
  accountElementExist: boolean;
  accountParams: string;
  accountParamsCountryExceptions: string;
  accountRegistrationJsonPath: string;
  accountSectionsJsonPath: string;
  accountType: string | number;
  accountVerification: 0 | 1;
  afterSuccessfulBet?: EAfterSuccessfulBet;
  allow_bonus_balance_for_superbet: boolean;
  aToZJsonPath?: string;
  aToZNavigationJsonPath?: string;
  betBuilder: boolean;
  betBuilderIframeUrl: string;
  betCardDefaultView: EBetCardView;
  betCopyLinkAvailable: boolean;
  betDownloadingAvailable: boolean;
  betFBSharingAvailable: boolean;
  betOccursumSharingAvailable: boolean;
  betSharingAvailable: boolean;
  betslipAddingPredefinedStakes: number;
  betslipDefaultStake: string;
  betslipDefaultStakeValue: number;
  betslipFloatingMode: boolean;
  betslipFloatingModeMobile: boolean;
  betslipFloatingPageType: boolean;
  betslipFloatingPages: number[];
  betslipFloatingPageTypeMobile: boolean;
  betslipFloatingPagesMobile: number[];
  betslipIcon?: string;
  betslipMaxBtn: number;
  betslipMaxBtnType: number;
  betslipPosition: boolean;
  betslipPredefinedStakes: string;
  betslipType: 0 | 1;
  betStatusCheck: boolean;
  betsWithSameGroup: number;
  betTelegramSharingAvailable: boolean;
  betTwitterSharingAvailable: boolean;
  betWhatsappSharingAvailable: boolean;
  betWithBonusOrDeposit: boolean;
  bookBetAvailable: boolean;
  bookingModeByDefault: boolean;
  bookBetSharingAvailable: boolean;
  bookCopyLinkAvailable: boolean;
  bookFBSharingAvailable: boolean;
  bookingDownloadingAvailable: boolean;
  bookingWhatsappAvailable: boolean;
  bookTelegramSharingAvailable: boolean;
  bookTwitterSharingAvailable: boolean;
  boostedOddsEnabled: boolean;
  bottomNavigation?: boolean;
  bottomNavigationBetslip?: boolean;
  carouselArrows: boolean;
  casinoCategoryElementExist: boolean;
  casinoFunMode: boolean;
  casinoGamblingLogoUrl?: string;
  casinoGameSideBar: boolean;
  casinoHighQualityPictures: boolean;
  casinoIframeFrozenBalance: boolean;
  casinoIframeMobile?: string;
  casinoLaunchType: number;
  casinoMultiView: number;
  casinoPopUpInMobile: boolean;
  casinoProviderElementExist: boolean;
  casinoUrl?: string;
  chainType: boolean;
  chunksPublicPath: string;
  cmsUrl?: string;
  communityChatEnabled: boolean;
  communityChatUserVerificationDisabled: boolean;
  couponBuilder?: boolean;
  currenciesWithSymbols: boolean;
  currencyPlacement: 'default' | 'left' | 'right';
  currentCountry: string;
  customIcons: boolean;
  customIconsURL: string;
  customPaymentUrl: string;
  customPeriodMaxDayLimit: string;
  dateDayMonthOrder: EDateDayMonthOrder;
  sendPreferredLanguage: number;
  decentralizedCasinoPartnerId?: number;
  decentralizedCasinoUrl?: string;
  defaultOddAccept: string;
  defaultOddTypes: any[];
  defaultSelectedOddType: string;
  depositAndPlay: boolean;
  depositPromo: boolean;
  domain: string;
  editBetAvailable: boolean;
  enableBottomNavigation?: boolean;
  bottomNavigationSubMenu?: boolean;
  enableLvision: boolean;
  errorMessageDuration: number;
  expressOfTheDayMobileLayout: EMultiplesView;
  flagStyle: EFlagStyle;
  gamblingAreas: Record<string, string[]>;
  gameNumber: boolean;
  generateSharingImageUrl: string;
  geoComplyEnabled: boolean;
  geoComplyUrl: string;
  geoLinkUrl: string;
  giftBet: string;
  GPSTrackingCheckingInterval: number;
  GPSTrackingEnabled: boolean;
  hideCasinoInfoIcon?: boolean;
  homeworkEnabled: boolean;
  hooryEnabled: boolean;
  hooryForElementsEnabled: boolean;
  iframe_sportsbook: boolean;
  includeLivePrematchFilter: boolean;
  includeSportDataInMetas: boolean;
  initiallyOpen: 'sportsTypes' | 'topLeagues';
  iovationEnabled: boolean;
  iovationEnabledX?: boolean;
  iovationMode: string;
  isCompact: string;
  isCompetitionCompact: number;
  isStreamActiveTabCompact: string;
  isTopNavigationButtonEnabled: boolean;
  isTwoFactorAvailable?: boolean;
  jurisdiction: EJurisdiction;
  kycType?: EKycType;
  leftSideBar?: boolean;
  leftSideBarAlwaysOpen?: boolean;
  leftSideBarBannerConfigs?: ILeftSideBarBannerConfigs;
  leftSideCasino?: boolean;
  leftSideSports?: boolean;
  locateGBGApiKey: string;
  logoUrl: string;
  lvisionAPiKey: string;
  mainHostName: string;
  marketsTooltips?: boolean;
  matchTrackerType: 0 | 1;
  metamaskLogin: boolean;
  multiplesEnabled: boolean;
  nemidIntegration: number;
  nemidIntegrationURL: string;
  olympicCompetition: number;
  olympicCompetitionLayout: number;
  openBetsByDefault: number;
  outrightFilter: number | string;
  pageParams: {
    changeFavoriteTeam: boolean;
    favoriteTeam: boolean;
    fixedScroll: boolean;
    jackpotForSportsbook?: boolean;
    pageLayout: ESportsbookLayoutTypes;
    pageType: string;
    racingSportsList?: ERacingSportSideBarTabs[];
    sportType: string;
    type: string;
  };
  partial_cashout_off_onn: boolean;
  partnerId?: number;
  personalizedSports: boolean;
  pictureInPictureStreaming?: boolean;
  postGridApiKey: string;
  previousBoostedOdds: boolean;
  priceSeparator: boolean;
  printBetAvailable: boolean;
  privacyPolicyLink: string;
  privacyPolicyTarget: boolean;
  pushNotifications: boolean;
  quickBet: boolean;
  realityCheckSwitcher: boolean;
  rebetAvailable: number;
  removeSuspendedSelection: boolean;
  renderCompetitionsInDom: boolean;
  rocketBet: boolean;
  sbPageUrls: SpringConfigsType['PAGE_URLS'];
  screenshotTimeout: number;
  sentryDisabled: boolean;
  seonIntegration?: boolean;
  seonValidationAction?: 'all' | 'login' | 'register';
  sessionTime?: number;
  showSiteNameInTitles: boolean;
  siteLogoUrl: string;
  siteName?: string;
  siteNameSeparatorInTitles?: '1' | '2' | '3';
  sportsbookIconStyle: ESportIconStyle;
  sportsConfigs: boolean;
  sportsGrouping: number;
  sportTournaments: boolean;
  springConfig: Record<string, any>;
  springPagesUrls: Record<string, string>;
  stake_1: number;
  stake_2: number;
  stake_3: number;
  statisticEnabled: boolean;
  statisticsLink: string;
  statisticsLinkLogoV3: string;
  statLink?: string;
  superBetDefaultOn: boolean;
  swarmUrl?: string;
  tabletByDefault: number;
  teamLogos?: string;
  teamLogosType?: ETeamLogosOptionTypes;
  terminalMode: boolean;
  termsLink: string;
  termsLinkTarget: boolean;
  termsPopupLink?: string;
  themeMode: number;
  timeOutCustomPeriod: string;
  todayEvents: number;
  topLeagues: number;
  topLeaguesDefaultOpen: boolean;
  topLeaguesLayout: ETopLeagueLayoutType;
  topMatches: number;
  topMatchesDefaultOpen: boolean;
  topMatchesLayout: ETopMatchesLayoutType;
  topNavigationButtonAlignment: tAlignment;
  topNavigationButtonPosition: tPosition;
  translationsVersion: number;
  useCustomDomainForStaticFiles: string;
  userFilesUrl: string;
  userPersonalizationEnabled: boolean;
  verificationType?: number;
  verifyAgePopup?: boolean;
  version: string;
  verificationKYCStatus?: boolean;
  matchTrackerViewType: string;
  lowBalanceWarningPopup?: string;
}

export interface IModuleConfigs {
  leftSideBar?: {
    languages?: {
      prefix: string;
      title: string;
      region: string;
      language: string;
      urlPrefix: string;
    }[];
    categoryIdsLeftSideBar?: string;
    categoryItemsLeftSideBar?: TCasinoCategoryItems;
  };
}

export interface ILanguageConfigs {
  id?: number;
  prefix: string;
  flag: string;
  title: string;
  region: string;
  language: string;
  swarmLangPrefix?: string;
  casinoLangPrefix?: string;
  default?: number;
  urlPrefix: string;
}

export enum ETeamLogosOptionTypes {
  LOGO = 'teamLogo',
  TSHIRT = 'teamShirt',
  LETTER = 'nameOnly'
}

export interface ICurrentPage {
  title: string;
  type: string;
  homepage: number;
  bottomNavBetslip?: boolean;
  seoParams: {
    pageTitle: string;
    description: string;
  };
  publishedIn?: Record<string, 0 | 1>;
}

export type SpringConfigsType = {
  PARTNER_ID: number;
  SOURCE: number | null;
  WRAPPER_APP: boolean;
  SWARM_URL: string;
  SWARM_BINARY_MESSAGE_ENABLED: boolean;
  CASINO_URL: string;
  DECENTRALIZED_CASINO_URL: string;
  DECENTRALIZED_CASINO_PARTNER_ID: number;
  JACKPOT_URL: string;
  GEO_LINK_URL: string;
  GEO_COMPLY_URL: string;
  A_TO_Z_MENU_DATA_PATH: string;
  ACCOUNT_REGISTRATION_JSON_PATH: string;
  ACCOUNT_PARAMS_COUNTRY_EXCEPTIONS: string;
  SECTIONS_JSON: string;
  ACCOUNT_PARAMS: string;
  USER_FILES_URL: string | boolean;
  TERMS_LINK: string;
  TERMS_POPUP_LINK: string;
  PRIVACY_POLICY_LINK: string;
  TERMS_LINK_TARGET: boolean;
  STORAGE_URL: string;
  PRIVACY_POLICY_TARGET: boolean;
  LANGUAGE_PREFIX: string;
  STATISTICS_LANG_PREFIX: string;
  MATCHTRACKER_LANG_PREFIX: string;
  CURRENT_LANGUAGE: string;
  CURRENT_LANGUAGE_PREFIX: string;
  DEFAULT_LANGUAGE_PREFIX: string;
  IS_DEFAULT_LANGUAGE?: number;
  ODD_ROUNDING: number;
  PRICE_ROUNDING: number;
  PRICE_SEPARATOR: string;
  HIDE_TRAILING_ZEROES: boolean;
  BOTTOM_NAV_VALIDATION?: boolean;
  SHOW_BETSLIP_IN_BOTTOM_NAV?: boolean;
  CASINO_DISABLE_FUN_MODE_FOR_NOT_AUTHORIZED_USERS?: boolean;
  VERSION: number;
  IS_TOP_NAVIGATION_BUTTON_ENABLED: boolean;
  TOP_NAVIGATION_BUTTON_POSITION: tPosition;
  TOP_NAVIGATION_BUTTON_ALIGNMENT: tAlignment;
  DIRECTION: string;
  SPORTS_CONFIGS: string | boolean;
  IOVATION_ENABLED: boolean;
  IOVATION_MODE: string;
  SCREENSHOT_TIMEOUT: number;
  LOGO_URL: string;
  INCLUDE_SPORT_DATA_IN_META?: boolean;
  SESSION_LIFE_TIME?: number;
  INACTIVITY_LOGOUT_ENABLED?: boolean;
  BOTTOM_NAVIGATION?: boolean;
  BOTTOM_NAVIGATION_SUB_MENU?: boolean;
  ERROR_MSG_DURATION?: number;
  IS_RTL: boolean;
  CMS_URL: string;
  SITE_LOGO_URL: string;
  CUSTOM_PAYMET_URL: string;
  USER_PERSONALIZATION_ENABLED: boolean;
  SEND_PREFERRED_LANGUAGE: number;
  DOC_NUMBER_REGISTRATION_AGE_OVER_18: boolean;
  DOC_NUMBER_REGISTRATION_PASSPORT_STRICT_FORMAT?: boolean;

  /**  CASINO  **/
  CASINO_GAMES_FETCH_COUNT: number;
  CASINO_GAMES_FETCH_MAX_COUNT: number;
  CASINO_CACHED_GAMES_MAX_COUNT: number;
  CASINO_MOBILE_IFRAME: boolean;
  CASINO_GAME_URL_PREFIX: string;
  CASINO_SPORTSBOOK_SWITCHER: number;
  CASINO_GAMBLING_LOGO_URL: string;
  CASINO_HIGH_QUALITY_PICTURES: boolean;
  CASINO_MOUNT_PATH: string;
  CASINO_GAME_SWITCHING_DURING_GAME_SESSION: boolean;
  CASINO_FUN_MODE: boolean;
  CASINO_GAME_SIDEBAR_ENABLED: boolean;
  CASINO_PROVIDER_ELEMENT_EXIST: boolean;
  CASINO_CATEGORY_ELEMENT_EXIST: boolean;
  CASINO_IFRAME_FROZEN_BALANCE: boolean;
  CASINO_POP_UP_IN_MOBILE: boolean;
  CASINO_LAUNCH_TYPE: number;
  CASINO_MULTIVIEW_ENABLED: number;
  /**/
  CASINO_TIMER_NET_WIN?: boolean;
  GAMBLING_COMMISSION?: boolean;
  PANIC_BUTTON?: boolean;
  /** CASINO CONFIGS FROM JURISDICTIONS **/
  SHOW_PLAYER_INFORMATION_IN_CASINO_SINGLE_VIEW?: boolean;
  SHOW_CASINO_TIMER_NET_WIN?: boolean;
  IS_HEADER_VISIBLE_IN_CASINO?: boolean;
  SHOW_GAMBLING_WITH_LOGO?: boolean;
  IS_CASINO_FUN_MODE_UNAVAILABLE?: boolean;
  DAILY_PLAYER_INFO?: boolean;
  GAMBLING_LiNKS?: boolean;

  /**  SPORTSBOOK  **/
  RENDER_COMPETITIONS_IN_DOM: boolean;
  SPORTSBOOK_MOUNT_PATH: string;
  SPORT_MENU_DEFAULT_SELECTED:
    | 'sportsTypes'
    | 'topLeagues'
    | 'todayEvents'
    | 'topMatches'
    | 'betBuilder'
    | 'multiples'
    | 'outright'
    | 'boostedOdds';
  SPORT_MARKET_IS_COMPACT: string | number;
  SPORT_COMPETITION_IS_COMPACT: number;
  IS_BOOK_BET_AVAILABLE: boolean;
  IS_BOOK_BET_SHARING_AVAILABLE: boolean;
  IS_BOOK_COPY_LINK_AVAILABLE: boolean;
  IS_BOOK_FACEBOOK_SHARING_AVAILABLE: boolean;
  IS_BOOK_TELEGRAM_SHARING_AVAILABLE: boolean;
  IS_BOOK_TWITTER_SHARING_AVAILABLE: boolean;
  IS_BOOK_WHATSAPP_SHARING_AVAILABLE: boolean;
  IS_BOOKING_DOWNLOAD_AVAILABLE: boolean;
  IS_BOOK_BET_MODE_BY_DEFAULT: boolean;
  IS_BET_SHARING_AVAILABLE: boolean;
  IS_BET_FACEBOOK_SHARING_AVAILABLE: boolean;
  IS_BET_TELEGRAM_SHARING_AVAILABLE: boolean;
  IS_BET_TWITTER_SHARING_AVAILABLE: boolean;
  IS_BET_WHATSAPP_SHARING_AVAILABLE: boolean;
  IS_BET_COMMUNTYCHAT_SHARING_AVAILABLE?: boolean;
  IS_BET_DOWNLOAD_AVAILABLE: boolean;
  IS_BET_COPY_LINK_AVAILABLE: boolean;
  GENERATE_SHARING_IMAGE_URL: string;
  IS_BET_STATUS_CHECKER_AVAILABLE: boolean;
  IS_QUICK_BET: boolean;
  GAME_NUMBER: boolean | number;
  IS_EDIT_BET_AVAILABLE: boolean;
  IS_BOOSTED_ODDS_ENABLED: boolean;
  PREVIOUS_BOOSTED_ODDS: boolean;
  IS_MULTIPLES_ENABLED: boolean;
  MULTIPLES_VIEW: EMultiplesView;
  IS_BETSLIP_FLOATING_ENABLED?: boolean;
  IS_BETSLIP_FLOATING_ENABLED_MOBILE?: boolean;
  BETSLIP_FLOATING_PAGES_TYPE?: boolean;
  BETSLIP_FLOATING_PAGES?: number[];
  BETSLIP_FLOATING_PAGES_TYPE_MOBILE?: boolean;
  BETSLIP_FLOATING_PAGES_MOBILE?: number[];
  BETSLIP_POSITION: boolean;
  BET_CARD_DEFAULT_VIEW: EBetCardView;
  TEAM_LOGOS: boolean;
  TEAM_LOGOS_TYPE?: ETeamLogosOptionTypes;
  PUSH_NOTIFICATIONS_ENABLED: boolean;
  BETS_WITH_SAME_GROUP: boolean;
  BETSLIP_MAX_BTN: boolean | number;
  BETSLIP_MAX_BTN_TYPE: number;
  SHOW_MULTIPLE_BET_ORIGINAL_TOTAL_ODDS: boolean;
  CUSTOM_ICONS: boolean;
  CUSTOM_ICONS_URL: string;
  SPORT_ICON_STYLE: ESportIconStyle;
  FLAG_STYLE: EFlagStyle;
  BETSLIP_TYPE: number;
  BETSLIP_BASE_UPDATE: boolean;
  BET_WITH_ONLY_BONUS_OR_DEPOSIT: boolean;
  STATISTICS_URL: string;
  STATISTICS_SWITCHER: boolean;
  MATCH_TRACKER_URL: string;
  MATCH_TRACKER_TYPE: string | number;
  TOP_LEAGUES: boolean;
  TOP_LEAGUES_LAYOUT: ETopLeagueLayoutType;
  TOP_LEAGUES_DEFAULT_OPEN: boolean;
  TOP_MATCHES: boolean;
  TOP_MATCHES_LAYOUT: ETopMatchesLayoutType;
  OLYMPIC_COMPETITION: boolean;
  OLYMPIC_COMPETITION_LAYOUT: EOlympicLayoutType;
  TODAY_EVENTS: boolean;
  MARKETS_TOOLTIPS: boolean;
  CALCULATE_POSSIBLE_WIN_WORKER_URL: string;
  BETSLIP_ICON?: string;
  CONFLICT_MESSAGE_IN_FOOTER?: boolean;

  /** SPORTSBOOK CONFIGS FROM JURISDICTIONS **/

  IS_AMERICAN_LAYOUT?: boolean;
  IS_SGB_TYPE_FOR_BB?: boolean;
  GET_AMERICAN_MARKET_TYPES?: boolean;
  SORT_UPCOMING_EVENTS_FIRST?: boolean;

  /** ACCOUNT **/
  USER_AUTOLOGIN?: boolean;
  WALLET_VIEW?: 'grid' | 'list';
  GIFT_BET: boolean;
  REBET_AVAILABLE?: boolean;
  IS_PRINT_AVAILABLE: boolean;
  REMOVE_SUSPENDED_SELECTION: boolean;
  METAMASK_LOGIN: boolean;
  THEME_SWITCHER: boolean;
  RESPONSIBLE_GAMING_URL: string;
  GAMBLING_MESSAGE_URL: string;
  POSTGRID_API_KEY: string | null;
  POSTGRID_API_URL: string;
  POSTGRID_API_INTERNATIONAL_URL: string;
  ACCOUNT_VERIFIED: boolean | number;
  SHOW_SELECT_BY_CITY?: boolean;
  DEPOSIT_PROMO: boolean;
  LOCATE_GBG_API_KEY: string | null;
  /**/
  LOGIN_FREEZE?: boolean;
  ADD_INFO_AFTER_LOGIN?: boolean;
  ACCOUNT_CLOSURE?: boolean;
  REALITY_CHECK?: boolean;
  SESSION_LIMIT?: boolean;
  SELF_EXCLUSION_REASON?: boolean;
  SELF_EXCLUDE_WARRNING_MESSAGE?: boolean;
  SESSION_INFORMATION_AVAILABLE?: boolean;
  SEON_INTEGRATION?: boolean;
  SEON_VALIDATION_ACTION: 'all' | 'login' | 'register';
  GPS_TRACKING_URL: string;
  GPS_TRACKING_ENABLED: boolean;
  GPS_TRACKING_CHECKING_INTERVAL: number;
  ACCOUNT_ELEMENT_EXIST: boolean;
  PLAYER_ACTIVITY_CHECK?: boolean;
  LOW_BALANCE_WARNING_POPUP?: boolean;
  /** ACCOUNT CONFIGS FROM JURISDICTIONS **/

  SHOW_DEPOSIT_LIMITS?: boolean;
  SHOW_DOCUMENTS_WITH_CARDS?: boolean;
  VALIDATE_DE_ADDRESS_FIELD?: boolean;
  SELFIE_WITH_DOCUMENTS?: boolean;
  SHOULD_HIDE_LIMITS_INITIAL_VALUE?: boolean;
  SHOW_USER_UNVERIFIED_MESSAGE?: boolean;
  SHOW_BRAZIL_USER_UNVERIFIED_MESSAGE?: boolean;
  SHOW_NICK_NAME_IN_REG_FORM?: boolean;
  SHOW_DOC_ISSUE_WITH_SUGGESTIONS?: boolean;
  SHOW_ACCOUNT_STATEMENT?: boolean;
  SHOW_ACCOUNT_SESSION?: boolean;
  SHOW_ACCOUNT_GAME_INFORMATION_MODAL?: boolean;
  SHOW_PRODUCT_ID_IN_BALANCE_HISTORY?: boolean;
  SHOULD_INIT_IDIN_SERVICE?: boolean;
  IS_MULTI_ACCOUNT_DISABLED?: boolean;
  SHOW_VERIFY_ACCOUNT_MODAL?: boolean;
  SHOW_ACCOUNT_ADDITIONAL_INFO_MODAL?: boolean;
  SHOW_ADM_TRANSACTION_ID_IN_BALANCE_HISTORY?: boolean;
  SHOW_USERNAME_IN_REG_FORM?: boolean;
  SHOW_DEPOSIT_LIMIT_CREATION_DATE?: boolean;
  SHOW_INFO_POPUP_BEFORE_REASONS_IN_ACCOUNT_CLOSE?: boolean;
  SHOW_WITHDRAWAL_CHECKER_MODAL?: boolean;
  SHOW_ACCOUNT_VERIFICATION_SECTION?: boolean;
  HIDE_WAGERING_FACTOR?: boolean;
  IS_PREDEFINED_PAYMENT_METHODS_AVAILABLE?: boolean;
  SHOW_BANK_NAME_IN_PAYMENT?: boolean;
  SHOW_TIMEOUT_INFO?: boolean;
  IS_FAST_REGISTRATION_ENABLED?: boolean;
  SHOULD_IGNORE_USER_FROZEN_BALANCE?: boolean;
  HAS_BONUS_ELIGIBLE?: boolean;
  IS_BONUS_BALANCE_VIEW_EXPANDED_BY_DEFAULT?: boolean;
  SHOW_DEPOSIT_CONFIRMATION_MESSAGE?: boolean;
  IS_PAYMENT_INFO_TEXT_LONGER?: boolean;
  CANADIAN_ACCOUNT_TYPE?: boolean;
  CYPRUS_ACCOUNT_TYPE?: boolean;
  MGA_ACCOUNT_TYPE?: boolean;
  CASINO_BALANCE_IN_USER_BALANCE?: boolean;
  IS_ACCUMULATED_LOSSES_AVAILABLE?: boolean;
  SHOULD_HIDE_ACCUMULATED_PROFITS?: boolean;
  DEPOSIT_RESTRICTION?: boolean;
  AGE_REQUIREMENTS_NOTICE?: boolean;
  DE_PROVINCE_ALPHA2?: boolean;
  GAMBLING_MESSAGE_ENABLED?: boolean;
  AUTOFILL_DOC_ISSUE_CODE?: boolean;
  UPLOAD_DOCS_MIN_COUNT?: boolean | number;
  LOGIN_LIMIT_POPUP?: boolean;
  SHOW_CONFIRMATION_POPUP_FOR_LIMITS?: boolean;
  MIN_DEPOSIT_LIMIT?: number;
  HIDE_WITHDRAWAL_ALLOWED_DATE?: boolean;
  SHOW_ONLY_SINGLE_AND_MULTIPLE_ODDS_IN_BET_HISTORY?: boolean;
  OLYMPIC_ICON_OTHER?: boolean;
  GEO_COMPLY_ENABLED?: boolean;
  SHOW_ADDITIONAL_CHECKBOX_FOR_LOGIN?: boolean;
  FACIAL_VERIFICATION_BEFORE_ACTIONS?: boolean;
  WRONG_LOGIN_ATTEMPTS_UNLOCK_MODAL?: boolean;
  DISPLAY_FAILED_KYC_MESSAGE?: boolean;
  SHOW_KYC_ADVANCED_ACTIONS?: boolean;
  SHOW_KYC_STATUS_BAR?: boolean;
  SHOW_KYC_UPLOAD?: boolean;
  COMBINE_TWO_WALLET_TYPES_IN_LIMITS?: boolean;
  HIDE_CASINO_INFO_ICON?: boolean;
  HIDE_BONUS_BALANCE_IN_FROZEN_SECTION?: boolean;
  KYC_VERIFICATION_TYPE?: EKycType;
  TRANSLATE_BALANCE_HISTORY_CASINO_FILTERS?: boolean;
  TERMS_AND_CONDITIONS?: boolean;
  NOTIFICATION_ACTIVE_TAB?: boolean;
  IS_NOTIFICATIONS_INITIAL_OFF?: boolean;
  VERIFY_PLAYER_INFO?: boolean;
  PREVENT_MULTIPLE_BROWSER_TABS?: boolean;
  REJECT_ZERO_VALUE_FOR_LIMITS?: boolean;

  /**  MISC  **/
  MOCKED_DATA: boolean;
  JURISDICTION: EJurisdiction;
  IS_WEBP_SUPPORTED: boolean | null;
  ICONS_MOUNT_PATH: string;
  ICON_EXTENSION: string;
  DATE_DAY_MONTH_ORDER: EDateDayMonthOrder;
  DT: {
    shortDate: string;
    longDate: string;
    time: string;
    longTime: string;
  };
  /**/
  PAGE_URLS: Record<string, string> & {
    homepageKey?: string;
    live: string;
    prematch: string;
    casino: string;
    tournaments: string;
    esport: string;
    results: string;
    'live-casino': string;
  };
  FIREBASE_ENABLED: boolean;
  ONE_SIGNAL_APP_ID: string;
  FAST_TRACK_BRAND: string;
  FAST_TRACK_REWARDS: boolean;
  GAMBLING_AREAS?: Record<string, string[]>;
  COMMUNITY_CHAT_ENABLED: boolean;
  COMMUNITY_CHAT_USER_VERIFICATION_DISABLED: boolean;
  BET_BUILDER_URL: string;
  IS_ACTIVE_STREAM_TAB: boolean;
  OUTRIGHT_FILTER: number;
  CAROUSEL_ARROWS: boolean;
  BET_BUILDER_SWITCHER: boolean;
  CURRENCIES_WITH_SYMBOLS: boolean;
  CURRENCY_PLACEMENT: TCurrencyPlacement;
  REALITY_CHECK_SWITCHER: boolean;
  DEFAULT_ODD_ACCEPT: string;
  CURRENT_COUNTRY_CODE: string;
  ODD_TYPE: Array<string>;
  DEFAULT_ODD_TYPE: string;
  OPEN_BETS_BY_DEFAULT: number;
  PREDEFINE_STAKES_ENABLE: boolean;
  PREDEFINE_STAKES_ADDING: boolean;
  PREDEFINE_STAKE_1: number;
  PREDEFINE_STAKE_2: number;
  PREDEFINE_STAKE_3: number;
  IS_PARTIAL_CASHOUT_AVAILABLE: boolean;
  IS_ALLOW_BONUS_BALANCE_SUPERBET: boolean;
  IFRAME_SPORTSBOOK: boolean;
  HOMEWORK_ENABLED: boolean;
  SHOW_SITE_NAME_IN_TITLE: number;
  SPORT_TOURNAMENTS: boolean;
  EXTERNAL_PAYMENT_URL: string;
  DEFAULT_STAKE_ENABLED: boolean;
  DEFAULT_STAKE: number;
  ROCKET_BET: boolean;
  LVISION: boolean;
  LVISION_API_KEY: string;
  PERSONALIZED_SPORTS: boolean;
  SPORTS_GROUPING: number;
  NEM_ID_INTEGRATION: number;
  NEM_ID_INTEGRATION_URL: string;
  LEFT_SIDE_BAR: boolean;
  LEFT_SIDE_BAR_ALWAYS_OPEN: boolean;
  LEFT_SIDE_BAR_SPORTS: boolean;
  LEFT_SIDE_BAR_CASINO: boolean;
  LEFT_SIDE_BAR_CONFIGS?: ILeftSideBarBannerConfigs;
  SUPER_BET_DEFAULT_ON: boolean;
  COUPON_BUILDER_SWITCHER: boolean;
  PICTURE_IN_PICTURE_STREAMING: boolean;
  AFTER_SUCCESSFUL_BET: EAfterSuccessfulBet;
  DOC_UPLOAD_TRU_NARRATIVE?: boolean;
  LIVE_PREMATCH_SWITCHER?: boolean;
  TERMINAL_MODE: boolean;
  CHAIN_TYPE: boolean;
  IOVATION_HIDE_UPLOAD?: boolean;

  /** BETSLIP CONFIGS FROM JURISDICTIONS **/

  CALCULATE_BETS_WITHOUT_TAXES?: boolean;
  SHOULD_HIDE_BET_TYPE_CHECKER?: boolean;
  HIDE_BET_BUILDER?: boolean;
  DISABLE_ONLY_CYPRUS_MATCHES_BET?: boolean;

  /** GENERIC CONFIGS FROM JURISDICTIONS **/

  SHOW_REALITY_CHECK?: boolean;
  SHOW_GAMBLING_AREAS?: boolean;
  IS_TWO_WALLET_ACTIVE?: boolean;
  IS_NEM_ID_ENABLED?: boolean;
  SHOW_WITHDRAWAL_INFORMATION?: boolean;
  IS_BANK_ID_INTEGRATION_ENABLED?: boolean;
  USE_AMERICAN_DATE_FORMATTING?: boolean;
  USE_CA_USA_DATE_FORMATTING?: boolean;
  HOORY_ENABLED: boolean;
  HOORY_ENABLED_SPORTSBOOK_BETSLIP: boolean;
  SENTRY_DISABLED: boolean;
  DEFAULT_TRUST_PERIOD_FOR_TWO_FACTOR_AUTH?: number;
  VERIFY_AGE_POPUP?: boolean;
  PAGCOR_POPUP?: boolean;
};
export enum ETypeOfLeftMenu {
  AToZ = 0,
  CryptoSideBarMobile = 1
}
export type TAtoZMenuConfigs = {
  fit: string;
  aToZRacingSportsList: ERacingSportSideBarTabs[];
  languages: {
    prefix: string;
    title: string;
    region: string;
    language: string;
    urlPrefix: string;
  }[];
  showLangSwitcher: boolean;
  selectCurrentPage: boolean;
  categoryIds: string;
  moduleId: string;
  type: string;
  category: string;
  limit: number;
  casinoGamesLimit: number;
  showCategoryName: number;
  outRightEnabledAToZ: boolean;
  todayEventsEnabledAToZ: boolean;
  boostedOddsEnabledAToZ: boolean;
  multiplesEnabledAToZ: boolean;
  sliderArrowsAToZ: boolean;
  couponBuilderAToZ: boolean;
  BBAToZ: boolean;
  topMatchesEnabledAToZ?: boolean;
  olympicCompetitionEnabledAtoZ?: boolean;
  aToZSportType?: string;
  typeOfLeftMenu?: ETypeOfLeftMenu;
};

export type AToZData = {
  category?: string;
  iconUrl?: string | null;
  id: number | string;
  order?: number;
  orderBottomNav?: number;
  path: { [key: string]: string } | string;
  showBottomNav?: boolean;
  showOnAtoZ: boolean;
  title: { [key: string]: string } | string;
  type?: string;
  icon?: null | string;
  showEvery?: boolean;
  onlyBottomNav?: boolean;
  badge?: boolean;
  inCasinoMenu?: boolean;
  inSportMenu?: boolean;
  menuCasinoOrdering?: number;
  menuSportsOrdering?: number;
  target?: string;
  customPageCodeEnable?: string;
  customPageCode?: string;
  customPageTitle?: string;
  customPageLogo?: string;
  categoryBottom?: string;
};

export type MenuItem = {
  id: number | string;
  alias: string;
  name: string;
  gameCount?: number;
  game?: number;
  showInLive?: boolean;
  order?: number;
  type?: number;
  afterIconIsRendered?: boolean;
};

export type TShowNoItemWrapper = {
  game?: boolean;
  competitions?: boolean;
  regions?: boolean;
};

export type TCurrentOdd = {
  sport: string;
  region: string;
  competition: string | number;
  game: number;
  category?: string;
};

export type THomeworkData = {
  answers: number;
  finalText: string;
  id: number;
  infoKey: string;
  infoUrl: string;
  language: string;
  openPopUp: boolean;
  tasks: number;
  type: number;
};

export type TSortingTypes = 'asc' | 'desc';

export interface ICustomMenuItem {
  key: number | string;
  value: string | ReactElement;
  icon?: ReactElement;
  count?: number;
  skeleton?: boolean;
}

export enum EFavoriteTabsTypes {
  ALL = 'all',
  SPORTSBOOK = '1',
  CASINO = '2'
}

export type FTNScan = {
  eventName?: string;
  blockNumber?: number;
  timeStamp: number;
  transactionHash: string;
  address?: string;
  name: string;
  amount: number;
}[];

export enum AppendCustomCodes {
  APPEND_CUSTOM_CODE_ON_REG_POPUP = 'appendCustomCodeRegistrationPopupOpen',
  CUSTOM_CODE_ON_REG_POPUP = 'customCodeRegistrationPopupOpen',
  APPEND_CUSTOM_CODE_ON_SIGN_IN_POPUP = 'appendCustomCodeSignInPopupOpen',
  CUSTOM_CODE_ON_SIGN_IN_POPUP = 'customCodeSignInPopupOpen',
  APPEND_CUSTOM_CODE_LOGIN = 'appendCustomCodeLogin',
  CUSTOM_CODE_LOGIN = 'customCodeLogin',
  APPEND_CUSTOM_CODE_LOGOUT = 'appendCustomCodeLogout',
  CUSTOM_CODE_LOGOUT = 'customCodeLogout',
  APPEND_CUSTOM_CODE_REG = 'appendCustomCodeRegistration',
  CUSTOM_CODE_REG = 'customCodeRegistration',
  APPEND_CUSTOM_CODE_DEPOSIT = 'appendCustomCodeDeposit',
  CUSTOM_CODE_DEPOSIT = 'customCodeDeposit',
  APPEND_CUSTOM_CODE_BETSLIP = 'betslipAppendCustomCodeActivity',
  CUSTOM_CODE_BETSLIP = 'betslipCustomCode',
  APPEND_CUSTOM_CODE_FIRST_DEPOSIT = 'appendCustomCodeFirstDeposit',
  CUSTOM_CODE_FIRST_DEPOSIT = 'customCodeFirstDeposit',
  APPEND_CUSTOM_CODE_DEPOSIT_FAIL = 'appendCustomFailedDeposit',
  CUSTOM_CODE_DEPOSIT_FAIL = 'customFailedDeposit',
  APPEND_CUSTOM_CODE_LOGIN_FAIL = 'appendCustomFailedLogin',
  CUSTOM_CODE_LOGIN_FAIL = 'customFailedLogin',
  APPEND_CUSTOM_CODE_REGISTER_FAIL = 'appendCustomCodeRegistrationWithErrors',
  CUSTOM_CODE_REGISTER_FAIL = 'customCodeRegistrationWithErrors',
  APPEND_CUSTOM_CODE_RESTORE_LOGIN = 'appendCustomCodeRestoreLogin',
  CUSTOM_CODE_RESTORE_LOGIN = 'customCodeRestoreLogin',
  APPEND_CUSTOM_CODE_WITHDRAW = 'appendCustomCodeWithdraw',
  CUSTOM_CODE_WITHDRAW = 'customCodeWithdraw',
  APPEND_CUSTOM_PROFILE_UPDATE = 'appendCustomCodeProfileUpdate',
  CUSTOM_CODE_PROFILE_UPDATE = 'customCodeProfileUpdate',
  APPEND_CUSTOM_EMAIL_CONFIRM = 'appendCustomCodeAfterEmailConfirmation',
  CUSTOM_CODE_EMAIL_CONFIRM = 'customCodeAfterEmailConfirmation',
  APPEND_CUSTOM_TIME_OUT = 'appendCustomCodeAfterSuccessfulTimeOut',
  CUSTOM_CODE_TIME_OUT = 'customCodeAfterSuccessfulTimeOut',
  APPEND_CUSTOM_SELF_EXC = 'appendCustomCodeSelfExclusion',
  CUSTOM_CODE_SELF_EXC = 'customCodeSelfExclusion',
  APPEND_CUSTOM_ON_DEPOSIT_PAGE = 'appendCustomCodeOnDepositPage',
  CUSTOM_CODE_ON_DEPOSIT_PAGE = 'customCodeOnDepositPage'
}

export type AccountParamsType = Record<string, AppendCustomCodes> | undefined;

export type JweResponse = Omit<ResponseGeneric, 'details'> & {
  details: {
    jwe: string;
  };
};

export type BetJweResponse = Omit<ResponseGeneric, 'details'> & {
  details: {
    bet_jwe: string;
  };
};

export enum EJurisdiction {
  Default = '0',
  Uk = '1',
  Italy = '2',
  Poland = '3',
  France = '4',
  MGA = '5',
  Belarus = '6',
  USA = '7',
  Canada = '8',
  Netherlands = '9',
  Ghana = '10',
  Sweden = '11',
  Germany = '12',
  Cyprus = '13',
  Ukraine = '14',
  Denmark = '15',
  SouthAfrica = '16',
  Brazil = '17',
  PAGCOR = '18',
  Colombia = '19',
  Peru = '20'
}

export enum ESearchType {
  SPORTSBOOK = 0,
  CASINO = 1
}

export enum ESearchLayout {
  Input = 0,
  Icon = 1
}

export enum EKycType {
  Signicat = 0,
  Legitimuz = 1
}
